/**
 * Token Storage Module
 * Replaces: Windows Registry (HKEY_CURRENT_USER\SOFTWARE\Microsoft\Office\Excel\Addins\InSyncAddin.AddinModule)
 * Original: AddinModule.cs registry operations throughout
 * 
 * Uses OfficeRuntime.storage for secure, sandboxed storage
 */

import { TEMP_TOKEN } from '../../utils/config';
import { clearIdleSession, isIdleExpired } from './idleSession';
import { TypedMemoryCache } from '../cache/memoryCache';

// Storage key constants (equivalent to registry value names)
const STORAGE_KEYS = {
  AUTH_TOKEN: 'iad_auth_token',           // Replaces: TokenID
  LOGIN_TIMESTAMP: 'iad_login_timestamp', // Replaces: Width (misnamed in original)
  USERNAME: 'iad_username',               // Replaces: UName
  FULLNAME: 'iad_fullname',               // NEW: FullName from API
  CLIENT_INTERNAL_ID: 'iad_client_internal_id', // Replaces: ClientInternalID
  RMS_ID: 'iad_rms_id',
  USER_ID: 'iad_user_id',
  DEVICE_STATUS: 'iad_device_status',
  FULL_ACCESS: 'iad_full_access',
  VALUATION_VISIBLE: 'iad_valuation_visible',
  LOGIN_MESSAGE: 'iad_login_message',
  PROFILE_TICKER_TYPE: 'iad_profile_ticker_type',
  PROFILE_NULL_RETURN_VALUE: 'iad_profile_null_return_value',
  PROFILE_KEYBOARD_SHORTCUT: 'iad_profile_keyboard_shortcut',
  LAST_LOGIN_DATE: 'iad_last_login_date', // Replaces: llt (MM-dd-yyyy)
  DEVELOPER_MODE: 'iad_developer_mode',   // Replaces: DeveloperNote
  CACHE_VERSION: 'iad_cache_version',     // Replaces: Refresh
  USER_TEMP_TOKEN: 'iad_user_temp_token', // Per-user encrypted token used as username_filter; falls back to env TEMP_TOKEN
  EDGE_TOKEN: 'iad_edge_token',           // Short-lived signed edge session token (VAPT V-001); minted by nginx at login, sent on data calls
} as const;

export interface LoginMetadata {
  rmsId?: string;
  userId?: string;
  deviceStatus?: string;
  fullAccess?: string;
  valuationVisible?: string;
  message?: string;
}

/**
 * Storage backend interface
 * Abstracts OfficeRuntime.storage vs localStorage
 */
interface StorageBackend {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

class OfficeRuntimeStorageBackend implements StorageBackend {
  private runtimeStorage: any;

  constructor() {
    // Access via window to avoid direct reference issues in non-Office contexts
    this.runtimeStorage = (window as any).OfficeRuntime?.storage;
    if (!this.runtimeStorage) {
      throw new Error('OfficeRuntime.storage not available');
    }
  }

  async getItem(key: string): Promise<string | null> {
    return this.runtimeStorage.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    return this.runtimeStorage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    return this.runtimeStorage.removeItem(key);
  }
}

class LocalStorageBackend implements StorageBackend {
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }
}

/**
 * Token Storage Service
 * 
 * Migration mapping from Registry:
 * - Registry: HKEY_CURRENT_USER\SOFTWARE\Microsoft\Office\Excel\Addins\InSyncAddin.AddinModule
 * - Office.js: OfficeRuntime.storage (encrypted per-user storage)
 */
export class TokenStorage {
  private storage: StorageBackend | null = null;
  private storageChecked = false;
  // In-memory copy of the logged-in username so callers (e.g. HttpClient) can
  // read it synchronously without an async storage round-trip per request. Kept
  // in sync by setAuthToken/clearAuth/getUsername.
  private cachedUsername: string | null = null;
  // In-memory copy of the per-user temp token (username_filter), same rationale
  // as cachedUsername. Kept in sync by setUserTempToken/clearAuth/getUserTempToken.
  private cachedUserTempToken: string | null = null;
  private cachedEdgeToken: string | null = null;

  constructor() {
    // Don't initialize in constructor - do lazy initialization instead
    // This handles cases where OfficeRuntime isn't available immediately
  }

  private initStorage(): void {
    if (this.storageChecked) return;
    this.storageChecked = true;

    try {
      // OfficeRuntime.storage is available in custom functions runtime
      // and in task pane when running in Office context
      const hasOfficeRuntime = typeof (window as any).OfficeRuntime !== 'undefined' && (window as any).OfficeRuntime?.storage;
      if (hasOfficeRuntime) {
        this.storage = new OfficeRuntimeStorageBackend();
      } else if (typeof localStorage !== 'undefined') {
        console.warn('TokenStorage: Using localStorage fallback - OfficeRuntime.storage not available');
        this.storage = new LocalStorageBackend();
      } else {
        console.error('TokenStorage: No storage backend available');
      }
    } catch (error) {
      console.error('TokenStorage: Error initializing storage:', error);
      // Fallback to localStorage if OfficeRuntime fails
      if (typeof localStorage !== 'undefined') {
        this.storage = new LocalStorageBackend();
      }
    }
  }

  /**
   * Ensure storage is available
   */
  private ensureStorage(): void {
    this.initStorage();
    if (!this.storage) {
      throw new Error('Storage not available. Must run within Office context or browser with localStorage.');
    }
  }

  /**
   * Get auth token
   * Equivalent to: Registry.GetValue("TokenID")
   */
  async getAuthToken(): Promise<string | null> {
    this.ensureStorage();
    return await this.storage!.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Set auth token with timestamp and user info
   * Equivalent to: 
   *   Registry.SetValue("TokenID", token)
   *   Registry.SetValue("Width", DateTime.Now)
   *   Registry.SetValue("UName", username)
   *   Registry.SetValue("FullName", fullName)
   *   Registry.SetValue("llt", "MM-dd-yyyy")
   */
  async setAuthToken(
    token: string,
    username: string,
    fullName?: string,
    clientInternalID?: string,
    metadata: LoginMetadata = {}
  ): Promise<void> {
    this.ensureStorage();
    const now = new Date();
    const timestamp = now.getTime().toString();
    // Format: MM-dd-yyyy for last login date
    const lastLoginDate = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${now.getFullYear()}`;
    
    await this.storage!.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    // The istari login token is also the edge-auth token (VAPT V-001): the client
    // presents it as X-Insync-Token on data calls. Store + mirror it so the
    // custom-functions runtime can read it too.
    await this.setEdgeToken(token);
    await this.storage!.setItem(STORAGE_KEYS.LOGIN_TIMESTAMP, timestamp);
    await this.storage!.setItem(STORAGE_KEYS.USERNAME, username);
    // Cross-runtime mirror, same rationale as USER_TEMP_TOKEN below: the
    // custom-functions runtime gets its OWN OfficeRuntime.storage and cannot see
    // what the taskpane wrote here, but localStorage IS shared. Without this the
    // custom functions can't resolve the logged-in user and every IAD/IBD/IGD
    // request is logged by nginx with an empty `user_email`.
    this.mirrorToLocalStorage(STORAGE_KEYS.USERNAME, username);
    this.cachedUsername = username || null;
    await this.storage!.setItem(STORAGE_KEYS.LAST_LOGIN_DATE, lastLoginDate);
    if (fullName) {
      await this.storage!.setItem(STORAGE_KEYS.FULLNAME, fullName);
    }
    if (clientInternalID) {
      await this.storage!.setItem(STORAGE_KEYS.CLIENT_INTERNAL_ID, clientInternalID);
    }
    await this.setOptionalItem(STORAGE_KEYS.RMS_ID, metadata.rmsId);
    await this.setOptionalItem(STORAGE_KEYS.USER_ID, metadata.userId);
    await this.setOptionalItem(STORAGE_KEYS.DEVICE_STATUS, metadata.deviceStatus);
    await this.setOptionalItem(STORAGE_KEYS.FULL_ACCESS, metadata.fullAccess);
    await this.setOptionalItem(STORAGE_KEYS.VALUATION_VISIBLE, metadata.valuationVisible);
    await this.setOptionalItem(STORAGE_KEYS.LOGIN_MESSAGE, metadata.message);
    await this.setDefaultItemIfMissing(STORAGE_KEYS.PROFILE_TICKER_TYPE, 'InSync Ticker');
    await this.setDefaultItemIfMissing(STORAGE_KEYS.PROFILE_NULL_RETURN_VALUE, '"-"');
    await this.setDefaultItemIfMissing(STORAGE_KEYS.PROFILE_KEYBOARD_SHORTCUT, 'Enable');
  }

  private async setOptionalItem(key: string, value?: string): Promise<void> {
    if (value !== undefined && value !== null) {
      await this.storage!.setItem(key, String(value));
    } else {
      await this.storage!.removeItem(key);
    }
  }

  private async setDefaultItemIfMissing(key: string, value: string): Promise<void> {
    const existingValue = await this.storage!.getItem(key);
    if (!existingValue) {
      await this.storage!.setItem(key, value);
    }
  }

  /**
   * Clear auth data (logout)
   * Equivalent to: 
   *   Registry.SetValue("Width", "")
   *   Registry.SetValue("TokenID", "")
   *   Registry.SetValue("UName", "")
   *   Registry.SetValue("FullName", "")
   *   Registry.SetValue("llt", "")
   */
  async clearAuth(): Promise<void> {
    this.ensureStorage();
    this.cachedUsername = null;
    this.cachedUserTempToken = null;
    this.cachedEdgeToken = null;
    await this.storage!.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    await this.storage!.removeItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
    await this.storage!.removeItem(STORAGE_KEYS.USERNAME);
    await this.storage!.removeItem(STORAGE_KEYS.FULLNAME);
    await this.storage!.removeItem(STORAGE_KEYS.CLIENT_INTERNAL_ID);
    await this.storage!.removeItem(STORAGE_KEYS.RMS_ID);
    await this.storage!.removeItem(STORAGE_KEYS.USER_ID);
    await this.storage!.removeItem(STORAGE_KEYS.DEVICE_STATUS);
    await this.storage!.removeItem(STORAGE_KEYS.FULL_ACCESS);
    await this.storage!.removeItem(STORAGE_KEYS.VALUATION_VISIBLE);
    await this.storage!.removeItem(STORAGE_KEYS.LOGIN_MESSAGE);
    await this.storage!.removeItem(STORAGE_KEYS.LAST_LOGIN_DATE);
    await this.storage!.removeItem(STORAGE_KEYS.DEVELOPER_MODE);
    await this.storage!.removeItem(STORAGE_KEYS.USER_TEMP_TOKEN);
    await this.storage!.removeItem(STORAGE_KEYS.EDGE_TOKEN);
    // Clear the shared localStorage mirrors so a logged-out custom-functions
    // runtime doesn't keep resolving a stale per-user token or username.
    this.removeLocalStorageMirror(STORAGE_KEYS.USER_TEMP_TOKEN);
    this.removeLocalStorageMirror(STORAGE_KEYS.EDGE_TOKEN);
    this.removeLocalStorageMirror(STORAGE_KEYS.USERNAME);
    clearIdleSession();
    // User-scoped API caches. The ticker list (the user's entitlements) is
    // mirrored to localStorage and would otherwise outlive the session on a
    // shared machine. clearAuth is the one path every session end takes
    // (logout, idle timeout, server revocation, 30-day expiry).
    TypedMemoryCache.clearAll();
  }

  /**
   * Get login timestamp
   * Equivalent to: Registry.GetValue("Width")
   */
  async getLoginTimestamp(): Promise<number | null> {
    this.ensureStorage();
    const value = await this.storage!.getItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
    return value ? parseInt(value, 10) : null;
  }

  /**
   * Get username
   * Equivalent to: Registry.GetValue("UName")
   *
   * Falls back to the shared localStorage mirror, because the custom-functions
   * runtime has its own OfficeRuntime.storage and never sees the username the
   * taskpane wrote at login. When the primary store DOES answer (taskpane), the
   * mirror is refreshed so sessions that logged in before the mirror existed
   * heal themselves without forcing a re-login.
   */
  async getUsername(): Promise<string | null> {
    this.ensureStorage();
    let value = await this.storage!.getItem(STORAGE_KEYS.USERNAME);
    if (value) {
      this.mirrorToLocalStorage(STORAGE_KEYS.USERNAME, value);
    } else {
      value = this.readLocalStorage(STORAGE_KEYS.USERNAME);
    }
    this.cachedUsername = value;
    return value;
  }

  /**
   * Synchronous, best-effort read of the logged-in username from the in-memory
   * cache. Returns null until setAuthToken/getUsername has populated it (e.g.
   * right after a page reload). Used by HttpClient to tag requests without an
   * async storage read on the hot path; pair with ensureUsername() to hydrate.
   */
  getCachedUsername(): string | null {
    return this.cachedUsername;
  }

  /**
   * Store the per-user encrypted token (used as username_filter). Trimmed and
   * cached in memory. No-op on empty input.
   */
  async setUserTempToken(token: string): Promise<void> {
    this.ensureStorage();
    const clean = (token ?? '').trim();
    if (!clean) return;
    await this.storage!.setItem(STORAGE_KEYS.USER_TEMP_TOKEN, clean);
    // Cross-runtime mirror: Office gives the custom-functions runtime a SEPARATE
    // OfficeRuntime.storage (taskpane writes are invisible to it), but localStorage
    // IS shared across the taskpane and custom-functions runtimes (verified). Mirror
    // the token there so custom functions (IAD/IBD/IGD) can read the per-user token.
    this.mirrorToLocalStorage(STORAGE_KEYS.USER_TEMP_TOKEN, clean);
    this.cachedUserTempToken = clean;
  }

  /**
   * Per-user temp token. Always reads the shared OfficeRuntime.storage — it must
   * NOT short-circuit on the in-memory cache, or the custom-functions runtime
   * keeps the first token it ever saw and ignores a re-login / different user
   * (the token that login writes from the taskpane runtime). The cache is still
   * updated so the synchronous getter has a best-effort value.
   */
  async getUserTempToken(): Promise<string | null> {
    this.ensureStorage();
    let value = await this.storage!.getItem(STORAGE_KEYS.USER_TEMP_TOKEN);
    // The custom-functions runtime can't see the taskpane's OfficeRuntime.storage,
    // but localStorage IS shared — fall back to the mirror written at login.
    if (!value) value = this.readLocalStorage(STORAGE_KEYS.USER_TEMP_TOKEN);
    this.cachedUserTempToken = value;
    return value;
  }

  /** Synchronous best-effort read of the per-user temp token (null until hydrated). */
  getCachedUserTempToken(): string | null {
    return this.cachedUserTempToken;
  }

  /**
   * Store the short-lived edge session token minted by nginx at login (VAPT
   * V-001). Mirrored to localStorage like the per-user token so the
   * custom-functions runtime (IAD/IBD/IGD) can attach it to data calls too.
   */
  async setEdgeToken(token: string): Promise<void> {
    this.ensureStorage();
    const clean = (token ?? '').trim();
    if (!clean) return;
    await this.storage!.setItem(STORAGE_KEYS.EDGE_TOKEN, clean);
    this.mirrorToLocalStorage(STORAGE_KEYS.EDGE_TOKEN, clean);
    this.cachedEdgeToken = clean;
  }

  /** Edge session token; reads shared storage, falling back to the localStorage mirror. */
  async getEdgeToken(): Promise<string | null> {
    // OTP session idle past its timeout: treat as logged out for data calls
    // (also in the custom-functions runtime) until the taskpane logs it out.
    if (isIdleExpired()) return null;
    this.ensureStorage();
    let value = await this.storage!.getItem(STORAGE_KEYS.EDGE_TOKEN);
    if (!value) value = this.readLocalStorage(STORAGE_KEYS.EDGE_TOKEN);
    // Migration (VAPT V-001): users logged in before the edge token existed have
    // no EDGE_TOKEN but do have their istari login token stored. Seed the edge
    // token from it so they migrate WITHOUT re-login. Write-through populates the
    // localStorage mirror the custom-functions runtime reads. (In the taskpane
    // runtime AUTH_TOKEN is available; the custom-functions runtime then picks up
    // the mirror once the taskpane has seeded it.)
    if (!value) {
      const authToken = await this.storage!.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (authToken) {
        value = authToken;
        try { await this.storage!.setItem(STORAGE_KEYS.EDGE_TOKEN, authToken); } catch { /* best-effort */ }
        this.mirrorToLocalStorage(STORAGE_KEYS.EDGE_TOKEN, authToken);
      }
    }
    this.cachedEdgeToken = value;
    return value;
  }

  /** Synchronous best-effort read of the edge token (null until hydrated). */
  getCachedEdgeToken(): string | null {
    if (isIdleExpired()) return null;
    return this.cachedEdgeToken;
  }


  private mirrorToLocalStorage(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* not available in some contexts */ }
  }

  private readLocalStorage(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  }

  private removeLocalStorageMirror(key: string): void {
    try { localStorage.removeItem(key); } catch { /* best-effort */ }
  }

  /**
   * Diagnostic only: which storage backend initStorage() latched onto for THIS
   * runtime — 'OfficeRuntime' (shared across taskpane + custom-functions
   * runtimes) vs 'localStorage' (per-runtime, NOT shared) vs 'none'. Used to
   * detect the custom-functions runtime falling back to localStorage and thus
   * never seeing tokens the taskpane stored in OfficeRuntime.storage.
   */
  getStorageBackendName(): 'OfficeRuntime' | 'localStorage' | 'none' {
    try {
      this.initStorage();
    } catch {
      /* ignore — report 'none' below */
    }
    if (this.storage instanceof OfficeRuntimeStorageBackend) return 'OfficeRuntime';
    if (this.storage instanceof LocalStorageBackend) return 'localStorage';
    return 'none';
  }

  /**
   * The username_filter to send with data APIs. Login is required:
   *   1. the user's own per-user encrypt token (from InSyncAuthenticationEncrypt), else
   *   2. the shared env TEMP_TOKEN — but ONLY for a logged-in user who doesn't have
   *      their own token yet (migration bridge for pre-encrypt 30-day sessions), else
   *   3. "" — not logged in → data APIs get no filter and return nothing.
   * The env token is never used anonymously, so formulas don't work without login.
   */
  async getUsernameFilter(): Promise<string> {
    if (isIdleExpired()) return "";
    try {
      const userToken = await this.getUserTempToken();
      if (userToken) return userToken;
      // Bridge: shared token only when there IS a login session (auth TokenKey).
      const loggedIn = !!(await this.getAuthToken());
      if (loggedIn && TEMP_TOKEN) return TEMP_TOKEN;
    } catch {
      /* fall through to no-token */
    }
    return "";
  }

  /**
   * Synchronous variant of getUsernameFilter for sync call sites: the cached
   * per-user token, else the shared env TEMP_TOKEN. Returns TEMP_TOKEN until the
   * cache is hydrated (after login or a getUserTempToken()/hydrate call), so the
   * worst case is a harmless fall back to the shared token — never a hard failure.
   */
  getUsernameFilterCached(): string {
    if (isIdleExpired()) return "";
    // Every pane is a fresh page load with a cold cache, and TEMP_TOKEN is empty
    // in prod builds (VAPT V-001). Read the localStorage mirror written at login,
    // synchronously, so a pane that logs before its first API call (e.g. Profile)
    // does not POST addin-logs without username_filter and get a 422.
    if (this.cachedUserTempToken === null) {
      const mirrored = this.readLocalStorage(STORAGE_KEYS.USER_TEMP_TOKEN);
      if (mirrored) this.cachedUserTempToken = mirrored;
    }
    return this.cachedUserTempToken ?? TEMP_TOKEN;
  }

  /**
   * Return the cached username, hydrating once from storage if it isn't loaded
   * yet (survives page reloads / auto-login). Never throws.
   */
  async ensureUsername(): Promise<string | null> {
    if (this.cachedUsername) return this.cachedUsername;
    try {
      return await this.getUsername();
    } catch {
      return null;
    }
  }

  /**
   * Get full name
   * Equivalent to: Registry.GetValue("FullName")
   */
  async getFullName(): Promise<string | null> {
    this.ensureStorage();
    return await this.storage!.getItem(STORAGE_KEYS.FULLNAME);
  }

  /**
   * Get client internal ID
   * Equivalent to: Registry.GetValue("ClientInternalID")
   */
  async getClientInternalID(): Promise<string | null> {
    this.ensureStorage();
    return await this.storage!.getItem(STORAGE_KEYS.CLIENT_INTERNAL_ID);
  }

  async getLoginMetadata(): Promise<LoginMetadata> {
    this.ensureStorage();
    const [rmsId, userId, deviceStatus, fullAccess, valuationVisible, message] = await Promise.all([
      this.storage!.getItem(STORAGE_KEYS.RMS_ID),
      this.storage!.getItem(STORAGE_KEYS.USER_ID),
      this.storage!.getItem(STORAGE_KEYS.DEVICE_STATUS),
      this.storage!.getItem(STORAGE_KEYS.FULL_ACCESS),
      this.storage!.getItem(STORAGE_KEYS.VALUATION_VISIBLE),
      this.storage!.getItem(STORAGE_KEYS.LOGIN_MESSAGE),
    ]);

    return {
      rmsId: rmsId || undefined,
      userId: userId || undefined,
      deviceStatus: deviceStatus || undefined,
      fullAccess: fullAccess || undefined,
      valuationVisible: valuationVisible || undefined,
      message: message || undefined,
    };
  }

  /**
   * Get last login date (MM-dd-yyyy)
   * Equivalent to: Registry.GetValue("llt")
   */
  async getLastLoginDate(): Promise<string | null> {
    this.ensureStorage();
    return await this.storage!.getItem(STORAGE_KEYS.LAST_LOGIN_DATE);
  }

  /**
   * Check if session is valid (30-day timeout, measured from login).
   * The original C# add-in used a 3-hour timeout (AddinModule.cs StartupCall(),
   * line 248-256); the session now lasts 30 days from the login timestamp.
   */
  async isSessionValid(): Promise<boolean> {
    const timestamp = await this.getLoginTimestamp();
    if (!timestamp) return false;

    const SESSION_MAX_AGE_DAYS = 30;
    const daysSinceLogin = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
    const isValid = daysSinceLogin < SESSION_MAX_AGE_DAYS;

    // If expired, clear credentials
    if (!isValid) {
      await this.clearAuth();
    }

    return isValid;
  }

  /**
   * Set developer mode flag
   * Equivalent to: Registry.SetValue("DeveloperNote", "DevelopedByInSync")
   * Original: AddinModule.cs adxRButtonLogin_OnClick() method, line 426
   */
  async setDeveloperMode(enabled: boolean): Promise<void> {
    this.ensureStorage();
    const value = enabled ? 'DevelopedByInSync' : '';
    if (enabled) {
      await this.storage!.setItem(STORAGE_KEYS.DEVELOPER_MODE, value);
    } else {
      await this.storage!.removeItem(STORAGE_KEYS.DEVELOPER_MODE);
    }
  }

  /**
   * Check if developer mode is enabled
   * Equivalent to: Registry.GetValue("DeveloperNote") == "DevelopedByInSync"
   */
  async isDeveloperMode(): Promise<boolean> {
    this.ensureStorage();
    const value = await this.storage!.getItem(STORAGE_KEYS.DEVELOPER_MODE);
    return value === 'DevelopedByInSync';
  }

  /**
   * Set cache refresh flag
   * Equivalent to: Registry.SetValue("Refresh", "1")
   * Original: CacheMangement.cs ClearAllCache() method, line 170
   */
  async setCacheRefreshFlag(): Promise<void> {
    this.ensureStorage();
    const version = Date.now().toString();
    await this.storage!.setItem(STORAGE_KEYS.CACHE_VERSION, version);
  }

  /**
   * Get and clear cache refresh flag
   * Equivalent to: Registry.GetValue("Refresh") == "1"
   */
  async getAndClearCacheRefreshFlag(): Promise<boolean> {
    this.ensureStorage();
    const value = await this.storage!.getItem(STORAGE_KEYS.CACHE_VERSION);
    await this.storage!.removeItem(STORAGE_KEYS.CACHE_VERSION);
    return !!value;
  }
}

// Singleton instance
export const tokenStorage = new TokenStorage();

export default tokenStorage;
